import React, { useState, useEffect, useMemo, useId } from "react";
import {
  Form,
  Row,
  Col,
  Button,
  InputGroup,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Badge,
} from "react-bootstrap";
import EmailApi from "../../api/EmailApi";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import LookupLayout from "../layout/LookupLayout";

function extractFieldNameFromToken(s) {
  if (!s || typeof s !== "string") return "";
  const t = s.trim();
  const m = t.match(/\{\{\s*([^}]+?)\s*\}\}/);
  return m ? m[1].trim() : t.replace(/[{}]/g, "").trim();
}

/** Labels for legacy saved placeholder-style recipients (e.g. old {{Owner.Email}}). */
const LEGACY_PLACEHOLDER_LABELS = {
  "{{CreatedBy.Email}}": "Created By Email",
  "{{Owner.Email}}": "Owner Email",
  "{{LastModifiedBy.Email}}": "Last Modified By Email",
};

/**
 * One-time migration from legacy toEmail / dynamicRecipients / configuredObjectEmailField.
 */
export function buildLegacyEmailSequence(config) {
  if (!config) return [];
  const seq = [];
  for (const e of config.toEmails || []) {
    if (e == null) continue;
    const s = String(e).trim();
    if (s) {
      seq.push({ kind: "static", email: s });
    }
  }
  for (const t of config.dynamicRecipients || []) {
    if (t && String(t).includes("{{")) {
      seq.push({ kind: "placeholder", token: String(t).trim() });
    }
  }
  const c = config.configuredObjectEmailField;
  if (c && String(c).trim()) {
    const fn = extractFieldNameFromToken(String(c));
    if (fn) {
      seq.push({ kind: "emailField", fieldName: fn });
    }
  }
  return seq;
}

function legacySequenceToDisplay(config) {
  if (Array.isArray(config?.emailRecipientSequence) && config.emailRecipientSequence.length > 0) {
    return config.emailRecipientSequence
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        if (item.kind === "static" && (item.email != null || item.value != null)) {
          return { kind: "static", email: String(item.email ?? item.value).trim() };
        }
        if (item.kind === "user" && (item.userId || item.id)) {
          return {
            kind: "user",
            userId: String(item.userId ?? item.id).trim(),
            displayName: item.displayName != null ? String(item.displayName) : "",
          };
        }
        if (item.kind === "placeholder" && item.token) {
          return { kind: "placeholder", token: String(item.token).trim() };
        }
        if (item.kind === "emailField" && item.fieldName) {
          return { kind: "emailField", fieldName: String(item.fieldName).trim() };
        }
        if (item.kind === "userLookup" && item.fieldName) {
          return { kind: "userLookup", fieldName: String(item.fieldName).trim() };
        }
        if (item.kind === "relatedEmailField" && item.lookupFieldName && item.fieldName) {
          return {
            kind: "relatedEmailField",
            lookupFieldName: String(item.lookupFieldName).trim(),
            fieldName: String(item.fieldName).trim(),
            targetObjectName:
              item.targetObjectName != null ? String(item.targetObjectName) : "",
            displayLabel: item.displayLabel != null ? String(item.displayLabel) : "",
          };
        }
        return null;
      })
      .filter(Boolean);
  }
  return buildLegacyEmailSequence(config);
}

export default function WorkflowSendEmail({
  config,
  onChange,
  onConfigPatch,
  objectName,
  readOnly,
}) {
  const [staticInput, setStaticInput] = useState("");
  const [staticInvalid, setStaticInvalid] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [allFields, setAllFields] = useState([]);
  const [pickUserLookup, setPickUserLookup] = useState("");
  const [pickEmailField, setPickEmailField] = useState("");
  const [activeParentLookup, setActiveParentLookup] = useState("");
  const [relatedObjectEmailFields, setRelatedObjectEmailFields] = useState([]);
  const [loadingRelatedObjectEmails, setLoadingRelatedObjectEmails] = useState(false);
  const [pickRelatedEmail, setPickRelatedEmail] = useState("");
  const [lookupRemountKey, setLookupRemountKey] = useState(0);
  const idPrefix = useId();

  const emailSequence = useMemo(
    () => legacySequenceToDisplay(config),
    [config]
  );

  const applySequence = (seq) => {
    if (onConfigPatch) {
      onConfigPatch({ emailRecipientSequence: seq });
    } else {
      onChange("emailRecipientSequence", seq);
    }
  };

  const emailRegex = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    []
  );

  const addStaticEmailsFromInput = () => {
    const parts = staticInput
      .split(/[,;\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const bad = parts.filter((p) => !emailRegex.test(p));
    if (bad.length > 0) {
      setStaticInvalid(true);
      return;
    }
    setStaticInvalid(false);
    const next = [...emailSequence];
    const seen = new Set(
      next.filter((i) => i.kind === "static").map((i) => i.email)
    );
    for (const p of parts) {
      const lo = p.toLowerCase();
      if (!seen.has(lo)) {
        seen.add(lo);
        next.push({ kind: "static", email: p });
      }
    }
    applySequence(next);
    setStaticInput("");
  };

  const addHardcodedUser = (userId, displayName) => {
    if (!userId || String(userId).trim() === "") return;
    const id = String(userId).trim();
    if (emailSequence.some((s) => s.kind === "user" && s.userId === id)) {
      setLookupRemountKey((k) => k + 1);
      return;
    }
    applySequence([
      ...emailSequence,
      { kind: "user", userId: id, displayName: displayName || "" },
    ]);
    setLookupRemountKey((k) => k + 1);
  };

  const addUserLookup = (fieldName) => {
    const fn = String(fieldName).trim();
    if (!fn) return;
    if (emailSequence.some((s) => s.kind === "userLookup" && s.fieldName === fn)) {
      setPickUserLookup("");
      return;
    }
    applySequence([...emailSequence, { kind: "userLookup", fieldName: fn }]);
    setPickUserLookup("");
  };

  const addEmailField = (fieldName) => {
    const fn = String(fieldName).trim();
    if (!fn) return;
    if (emailSequence.some((s) => s.kind === "emailField" && s.fieldName === fn)) {
      setPickEmailField("");
      return;
    }
    applySequence([...emailSequence, { kind: "emailField", fieldName: fn }]);
    setPickEmailField("");
  };

  const addRelatedObjectEmail = (
    lookupFieldName,
    targetObjectName,
    emailFieldName,
    displayLabel
  ) => {
    const lf = String(lookupFieldName).trim();
    const ef = String(emailFieldName).trim();
    if (!lf || !ef) return;
    if (
      emailSequence.some(
        (s) =>
          s.kind === "relatedEmailField" &&
          s.lookupFieldName === lf &&
          s.fieldName === ef
      )
    ) {
      setPickRelatedEmail("");
      return;
    }
    applySequence([
      ...emailSequence,
      {
        kind: "relatedEmailField",
        lookupFieldName: lf,
        targetObjectName: targetObjectName != null ? String(targetObjectName) : "",
        fieldName: ef,
        displayLabel: displayLabel != null ? String(displayLabel) : "",
      },
    ]);
    setPickRelatedEmail("");
  };

  const removeAt = (index) => {
    applySequence(emailSequence.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const load = async () => {
      if (!objectName) {
        setAllFields([]);
        return;
      }
      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(
          objectName
        );
        if (response.success && response.data?.fields) {
          setAllFields(response.data.fields);
        } else {
          setAllFields([]);
        }
      } catch (e) {
        console.error("WorkflowSendEmail: failed to load fields", e);
        setAllFields([]);
      }
    };
    load();
  }, [objectName]);

  const userLookupFields = useMemo(() => {
    if (!allFields.length) return [];
    const out = [];
    for (const f of allFields) {
      const dt = (f.data_type || "").toLowerCase();
      const ref = (f.reference_object || "").toLowerCase();
      const isUserRef =
        ref === "user" && (dt === "lookup" || f.is_reference === true);
      if (isUserRef) {
        out.push(f);
      }
    }
    return out.sort((a, b) => {
      if (a.field_name === "ownerid") return -1;
      if (b.field_name === "ownerid") return 1;
      return String(a.field_label || a.field_name).localeCompare(
        String(b.field_label || b.field_name)
      );
    });
  }, [allFields]);

  const hasOwnerInUserLookups = useMemo(
    () => userLookupFields.some((f) => f.field_name === "ownerid"),
    [userLookupFields]
  );

  const emailFieldsOnRecord = useMemo(() => {
    if (!allFields.length) return [];
    return allFields
      .filter((f) => (f.data_type || "").toLowerCase() === "email")
      .sort((a, b) =>
        String(a.field_label || a.field_name).localeCompare(
          String(b.field_label || b.field_name)
        )
      );
  }, [allFields]);

  /** Lookups to objects other than user (e.g. account) — for parent/related email picker. */
  const nonUserLookupFields = useMemo(() => {
    if (!allFields.length) return [];
    const out = [];
    for (const f of allFields) {
      const dt = (f.data_type || "").toLowerCase();
      const ref = (f.reference_object || "").toString().trim();
      if (!ref || ref.toLowerCase() === "user") continue;
      if (dt === "lookup" || f.is_reference === true) {
        out.push(f);
      }
    }
    return out.sort((a, b) =>
      String(a.field_label || a.field_name).localeCompare(
        String(b.field_label || b.field_name)
      )
    );
  }, [allFields]);

  const userLookupLabel = useMemo(() => {
    const m = new Map();
    m.set("ownerid", "Record owner (ownerid)");
    for (const f of userLookupFields) {
      m.set(f.field_name, f.field_label || f.field_name);
    }
    return m;
  }, [userLookupFields]);

  const emailFieldLabel = useMemo(() => {
    const m = new Map();
    for (const f of emailFieldsOnRecord) {
      m.set(f.field_name, f.field_label || f.field_name);
    }
    return m;
  }, [emailFieldsOnRecord]);

  const placeholderLabel = useMemo(() => {
    const m = new Map(Object.entries(LEGACY_PLACEHOLDER_LABELS));
    return m;
  }, []);

  useEffect(() => {
    if (!activeParentLookup) {
      setRelatedObjectEmailFields([]);
      return;
    }
    const meta = nonUserLookupFields.find((f) => f.field_name === activeParentLookup);
    const refObj = meta?.reference_object;
    if (!refObj) {
      setRelatedObjectEmailFields([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingRelatedObjectEmails(true);
      try {
        const res = await ObjectConfigApi.fetchConfigObjectByNameWithFields(refObj);
        if (cancelled) return;
        if (res.success && res.data?.fields) {
          const emails = res.data.fields
            .filter((x) => (x.data_type || "").toLowerCase() === "email")
            .sort((a, b) =>
              String(a.field_label || a.field_name).localeCompare(
                String(b.field_label || b.field_name)
              )
            );
          setRelatedObjectEmailFields(emails);
        } else {
          setRelatedObjectEmailFields([]);
        }
      } catch (e) {
        console.error("WorkflowSendEmail: load related object email fields", e);
        if (!cancelled) setRelatedObjectEmailFields([]);
      } finally {
        if (!cancelled) setLoadingRelatedObjectEmails(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeParentLookup, nonUserLookupFields]);

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!objectName) {
        setTemplates([]);
        return;
      }
      setLoadingTemplates(true);
      try {
        const emailApi = new EmailApi();
        const response = await emailApi.getEmailTemplates(objectName);
        if (response.success) {
          setTemplates(response.data || []);
        } else {
          setTemplates([]);
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
        setTemplates([]);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, [objectName]);

  useEffect(() => {
    if (templates.length > 0 && config.templateId) {
      const selectedTemplate = templates.find(
        (t) => t.id === config.templateId || t.id === parseInt(config.templateId, 10)
      );
      if (selectedTemplate) {
        if (selectedTemplate.subject) {
          onChange("subject", selectedTemplate.subject);
        }
        if (selectedTemplate.body) {
          onChange("body", selectedTemplate.body);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, config.templateId]);

  const handleTemplateChange = (templateId) => {
    if (!templateId) {
      onChange("templateId", null);
      onChange("subject", "");
      onChange("body", "");
      return;
    }
    onChange("templateId", templateId);
    const selectedTemplate = templates.find(
      (t) => t.id === templateId || t.id === parseInt(templateId, 10)
    );
    if (selectedTemplate) {
      onChange("subject", selectedTemplate.subject || "");
      onChange("body", selectedTemplate.body || "");
    }
  };

  const selectedTemplate = config.templateId
    ? templates.find(
        (t) => t.id === config.templateId || t.id === parseInt(config.templateId, 10)
      )
    : null;

  const renderChip = (item, index) => {
    if (item.kind === "static") {
      return (
        <span
          key={`s-${item.email}-${index}`}
          className="d-inline-block me-2 mb-1 align-middle"
        >
          <Badge bg="secondary" className="d-flex align-items-center gap-1 py-2 px-2">
            <span>
              <i className="fa fa-envelope me-1" />
              {item.email}
            </span>
            {!readOnly && (
              <Button
                variant="link"
                className="text-white p-0 ms-1 lh-1"
                size="sm"
                onClick={() => removeAt(index)}
                aria-label="Remove"
              >
                &times;
              </Button>
            )}
          </Badge>
        </span>
      );
    }
    if (item.kind === "user") {
      const label = item.displayName || `${String(item.userId).slice(0, 8)}…`;
      return (
        <span
          key={`u-${item.userId}-${index}`}
          className="d-inline-block me-2 mb-1 align-middle"
        >
          <Badge bg="secondary" className="d-flex align-items-center gap-1 py-2 px-2">
            <span>
              <i className="fa fa-user me-1" />
              {label}
            </span>
            {!readOnly && (
              <Button
                variant="link"
                className="text-white p-0 ms-1 lh-1"
                size="sm"
                onClick={() => removeAt(index)}
                aria-label="Remove"
              >
                &times;
              </Button>
            )}
          </Badge>
        </span>
      );
    }
    if (item.kind === "placeholder") {
      const label = placeholderLabel.get(item.token) || item.token;
      return (
        <span
          key={`p-${item.token}-${index}`}
          className="d-inline-block me-2 mb-1 align-middle"
        >
          <Badge bg="info" text="dark" className="d-flex align-items-center gap-1 py-2 px-2">
            <span>
              <i className="fa fa-bolt me-1" />
              {label}
            </span>
            {!readOnly && (
              <Button
                variant="link"
                className="text-dark p-0 ms-1 lh-1"
                size="sm"
                onClick={() => removeAt(index)}
                aria-label="Remove"
              >
                &times;
              </Button>
            )}
          </Badge>
        </span>
      );
    }
    if (item.kind === "userLookup") {
      const label = userLookupLabel.get(item.fieldName) || item.fieldName;
      return (
        <span
          key={`ul-${item.fieldName}-${index}`}
          className="d-inline-block me-2 mb-1 align-middle"
        >
          <Badge bg="primary" className="d-flex align-items-center gap-1 py-2 px-2">
            <span>
              <i className="fa fa-link me-1" />
              {label}
            </span>
            {!readOnly && (
              <Button
                variant="link"
                className="text-white p-0 ms-1 lh-1"
                size="sm"
                onClick={() => removeAt(index)}
                aria-label="Remove"
              >
                &times;
              </Button>
            )}
          </Badge>
        </span>
      );
    }
    if (item.kind === "emailField") {
      const label = emailFieldLabel.get(item.fieldName) || item.fieldName;
      return (
        <span
          key={`e-${item.fieldName}-${index}`}
          className="d-inline-block me-2 mb-1 align-middle"
        >
          <Badge bg="success" className="d-flex align-items-center gap-1 py-2 px-2">
            <span>
              <i className="fa fa-at me-1" />
              {label}
            </span>
            {!readOnly && (
              <Button
                variant="link"
                className="text-white p-0 ms-1 lh-1"
                size="sm"
                onClick={() => removeAt(index)}
                aria-label="Remove"
              >
                &times;
              </Button>
            )}
          </Badge>
        </span>
      );
    }
    if (item.kind === "relatedEmailField") {
      const ro = item.targetObjectName || "";
      const short =
        (item.displayLabel && String(item.displayLabel).trim()) ||
        (ro && item.fieldName
          ? `${ro}: ${item.fieldName}`
          : `${item.lookupFieldName} → ${item.fieldName}`);
      return (
        <span
          key={`re-${item.lookupFieldName}-${item.fieldName}-${index}`}
          className="d-inline-block me-2 mb-1 align-middle"
        >
          <Badge bg="warning" text="dark" className="d-flex align-items-center gap-1 py-2 px-2">
            <span title={`{{${item.lookupFieldName}.${item.fieldName}}}`}>
              <i className="fa fa-sitemap me-1" />
              {short}
            </span>
            {!readOnly && (
              <Button
                variant="link"
                className="text-dark p-0 ms-1 lh-1"
                size="sm"
                onClick={() => removeAt(index)}
                aria-label="Remove"
              >
                &times;
              </Button>
            )}
          </Badge>
        </span>
      );
    }
    return null;
  };

  return (
    <>
      <Form>
        <Row>
          <Col lg={12}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                Recipients
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip>
                      Add static addresses, a user, user lookups, this record&apos;s email fields,
                      or a related (non-user) lookup first and then an email on that object.
                      Merges use the same <code>{"{{lookupField.emailField}}"}</code> resolution as
                      elsewhere. &quot;To&quot; addresses are deduplicated when sending.
                    </Tooltip>
                  }
                >
                  <i className="fa fa-info-circle ms-2 text-muted" />
                </OverlayTrigger>
              </Form.Label>
              <div className="border rounded p-3 bg-light text-start">
                {!readOnly && (
                  <div className="d-flex flex-wrap align-items-end gap-3 w-100 justify-content-start mb-3">
                    <div style={{ minWidth: "200px", maxWidth: "100%", flex: "1 1 200px" }}>
                      <Form.Label className="small text-secondary mb-1 d-block">
                        Static email(s)
                      </Form.Label>
                      <InputGroup size="sm">
                        <Form.Control
                          type="text"
                          placeholder="one@example.com, other@org.com"
                          value={staticInput}
                          isInvalid={staticInvalid}
                          onChange={(e) => {
                            setStaticInput(e.target.value);
                            setStaticInvalid(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addStaticEmailsFromInput();
                            }
                          }}
                        />
                        <Button variant="primary" onClick={addStaticEmailsFromInput}>
                          Add
                        </Button>
                      </InputGroup>
                      {staticInvalid && (
                        <div className="text-danger small mt-1">Enter valid email address(es).</div>
                      )}
                    </div>
                    <div style={{ minWidth: "220px", maxWidth: "100%", flex: "1 1 220px" }}>
                      <Form.Label className="small text-secondary mb-1 d-block">
                        Add user
                      </Form.Label>
                      <LookupLayout
                        key={lookupRemountKey}
                        objectName="user"
                        searchFields="email,firstname,lastname"
                        displayFields="firstname,lastname"
                        value=""
                        defaultLabel=""
                        onChange={(userId, userName) => addHardcodedUser(userId, userName)}
                        disabled={readOnly}
                        placeholder="Search to add a user…"
                      />
                    </div>
                    <div style={{ minWidth: "220px", maxWidth: "100%" }}>
                      <Form.Label className="small text-secondary mb-1 d-block">
                        User lookup field
                      </Form.Label>
                      <Form.Select
                        id={`${idPrefix}-user-lookup`}
                        size="sm"
                        value={pickUserLookup}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPickUserLookup(v);
                          if (v) addUserLookup(v);
                        }}
                        disabled={!objectName}
                      >
                        <option value="">
                          {objectName ? "Choose user lookup…" : "Select object first"}
                        </option>
                        {!hasOwnerInUserLookups && (
                          <option value="ownerid">Record owner (ownerid)</option>
                        )}
                        {userLookupFields.map((f) => (
                          <option key={f.field_name} value={f.field_name}>
                            {f.field_name === "ownerid"
                              ? "Record owner (ownerid)"
                              : f.field_label || f.field_name}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                    <div style={{ minWidth: "220px", maxWidth: "100%" }}>
                      <Form.Label className="small text-secondary mb-1 d-block">
                        Email field (on record)
                      </Form.Label>
                      <Form.Select
                        id={`${idPrefix}-email-field`}
                        size="sm"
                        value={pickEmailField}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPickEmailField(v);
                          if (v) addEmailField(v);
                        }}
                        disabled={!objectName}
                      >
                        <option value="">
                          {objectName ? "Choose email field…" : "Select object first"}
                        </option>
                        {emailFieldsOnRecord.map((f) => (
                          <option key={f.field_name} value={f.field_name}>
                            {f.field_label || f.field_name}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  </div>
                )}
                {!readOnly && nonUserLookupFields.length > 0 && (
                  <div className="d-flex flex-wrap align-items-end gap-3 w-100 justify-content-start mb-3">
                    <div style={{ minWidth: "240px", maxWidth: "100%" }}>
                      <Form.Label className="small text-secondary mb-1 d-block">
                        Related object (lookup on this record)
                      </Form.Label>
                      <Form.Select
                        id={`${idPrefix}-parent-lookup`}
                        size="sm"
                        value={activeParentLookup}
                        onChange={(e) => {
                          const v = e.target.value;
                          setActiveParentLookup(v);
                          setPickRelatedEmail("");
                        }}
                        disabled={!objectName}
                      >
                        <option value="">
                          {objectName
                            ? "Choose a lookup to parent / related…"
                            : "Select object first"}
                        </option>
                        {nonUserLookupFields.map((f) => {
                          const ro = f.reference_object || "";
                          return (
                            <option key={f.field_name} value={f.field_name}>
                              {f.field_label || f.field_name}
                              {ro ? ` → ${ro}` : ""}
                            </option>
                          );
                        })}
                      </Form.Select>
                    </div>
                    <div style={{ minWidth: "240px", maxWidth: "100%" }}>
                      <Form.Label className="small text-secondary mb-1 d-block">
                        Email on related object
                      </Form.Label>
                      <Form.Select
                        id={`${idPrefix}-parent-email`}
                        size="sm"
                        value={pickRelatedEmail}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (!v || !activeParentLookup) return;
                          const lookupMeta = nonUserLookupFields.find(
                            (f) => f.field_name === activeParentLookup
                          );
                          const em = relatedObjectEmailFields.find(
                            (f) => f.field_name === v
                          );
                          const ro = lookupMeta?.reference_object || "";
                          const displayLabel =
                            ro && v
                              ? `${ro}: ${em?.field_label || v} (via ${
                                  lookupMeta?.field_label || activeParentLookup
                                })`
                              : "";
                          addRelatedObjectEmail(
                            activeParentLookup,
                            ro,
                            v,
                            displayLabel
                          );
                        }}
                        disabled={
                          !objectName ||
                          !activeParentLookup ||
                          loadingRelatedObjectEmails
                        }
                      >
                        <option value="">
                          {!activeParentLookup
                            ? "Select related lookup first"
                            : loadingRelatedObjectEmails
                              ? "Loading email fields…"
                              : relatedObjectEmailFields.length === 0
                                ? "No email fields on that object"
                                : "Choose email field…"}
                        </option>
                        {relatedObjectEmailFields.map((f) => (
                          <option key={f.field_name} value={f.field_name}>
                            {f.field_label || f.field_name}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  </div>
                )}
                <div className={readOnly ? "" : "pt-2 border-top border-light"}>
                  <div className="small text-secondary mb-2">To (in order added)</div>
                  <div className="text-start w-100" style={{ lineHeight: 1.8 }}>
                    {emailSequence.map((item, i) => renderChip(item, i))}
                    {emailSequence.length === 0 && (
                      <span className="text-muted small d-block">
                        {readOnly
                          ? "No recipients."
                          : "Add at least one recipient above."}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-1">
          <Col lg={12}>
            <Form.Group>
              <Form.Label>Email Template (Optional)</Form.Label>
              {loadingTemplates ? (
                <div className="text-muted">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading templates...
                </div>
              ) : (
                <Form.Select
                  value={config.templateId || ""}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">No template (use custom subject/body)</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Form.Select>
              )}
              <Form.Text className="text-muted">
                {templates.length > 0
                  ? `Select a template to use. The template will be fetched and used when the workflow runs. ${templates.length} template(s) available.`
                  : objectName
                    ? "No templates available for this object. You can create templates in the setup section."
                    : "Select an object to see available templates."}
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col lg={12}>
            <Form.Group>
              <Form.Label>
                Subject {!config.templateId && <span className="text-danger">*</span>}
                {selectedTemplate && (
                  <span className="text-muted ms-2">(Preview from template: {selectedTemplate.name})</span>
                )}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={selectedTemplate ? "Template subject preview" : "Enter email subject"}
                value={config.subject || ""}
                onChange={(e) => onChange("subject", e.target.value)}
                required={!readOnly && !config.templateId}
                disabled={readOnly || !!config.templateId}
                readOnly={!!config.templateId}
                style={config.templateId ? { backgroundColor: "#f8f9fa", cursor: "not-allowed" } : {}}
              />
              {config.templateId && selectedTemplate && (
                <Form.Text className="text-muted">
                  This is a preview of the subject from template &quot;{selectedTemplate.name}&quot;. The
                  template will be fetched and used when the workflow runs. Placeholders will be resolved
                  automatically.
                </Form.Text>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col lg={12}>
            <Form.Group>
              <Form.Label>
                Body {!config.templateId && <span className="text-danger">*</span>}
                {selectedTemplate && (
                  <span className="text-muted ms-2">(Preview from template: {selectedTemplate.name})</span>
                )}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                placeholder={selectedTemplate ? "Template body preview" : "Enter email body"}
                value={config.body || ""}
                onChange={(e) => onChange("body", e.target.value)}
                required={!readOnly && !config.templateId}
                disabled={readOnly || !!config.templateId}
                readOnly={!!config.templateId}
                style={config.templateId ? { backgroundColor: "#f8f9fa", cursor: "not-allowed" } : {}}
              />
              {config.templateId && selectedTemplate && (
                <Form.Text className="text-muted">
                  This is a preview of the body from template &quot;{selectedTemplate.name}&quot;. The
                  template will be fetched and used when the workflow runs. Placeholders like{" "}
                  {"{{FieldName}}"} will be resolved with actual values automatically.
                </Form.Text>
              )}
            </Form.Group>
          </Col>
        </Row>
      </Form>
    </>
  );
}
